import { CheckIcon, CloseIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Badge,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import React from "react";
import type { ResponseOf } from "../../../../../core/api/api-utils";
import RangeDatePicker from "../../../../../shared/components/DatePicker/RangeDatePicker";
import Select from "../../../../../shared/components/Select";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import { EntityProfileEmptyState } from "../../../../entityProfileModal/components/EntityProfileFeedback";
import EntityProfileFilterBar, {
  EntityProfileFilterBarItem,
} from "../../../../entityProfileModal/components/EntityProfileFilterBar";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import type { CaregiverProfileBridgeActions } from "../../../caregiverProfileModal.actions";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import { useCaregiverUploadedDocuments } from "./CaregiverProfileDocumentsSection.hooks";

type UploadedDocumentsResponse = ResponseOf<
  "get",
  "/agencies/:agencyId/caregivers/:caregiverId/uploaded_documents"
>;
type CaregiverDocument = UploadedDocumentsResponse["documents"][number];
type CaregiverDocumentUpload = CaregiverDocument["uploads"][number];
type DocumentSortKey =
  | "category"
  | "documentType"
  | "uploads"
  | "isHr"
  | "isCompliance"
  | "isPassport";
type FlagFilter = "all" | "yes" | "no";

type Props = {
  actions: CaregiverProfileBridgeActions;
  caregiver?: CaregiverProfileCaregiver;
  caregiverId: CaregiverId;
};

const flagFilterOptions: Array<{ value: FlagFilter; label: string }> = [
  { value: "all", label: "Select" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const filterSelectButtonProps: React.ComponentProps<typeof Button> = {
  bg: "#fff",
  borderColor: "#cfcfcf",
  borderRadius: "3px",
  boxShadow: "none",
  color: "#111827",
  colorScheme: "gray",
  fontSize: "14px",
  fontWeight: 600,
  h: "34px",
  lineHeight: "34px",
  minW: 0,
  px: 3,
  w: "100%",
  _active: { bg: "#fff", borderColor: "#adadad", boxShadow: "none" },
  _hover: { bg: "#fff", borderColor: "#adadad" },
};

function parseLocalDate(value: unknown): LocalDate | null {
  if (value instanceof LocalDate) {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  try {
    return LocalDate.parse(value.slice(0, 10));
  } catch {
    return null;
  }
}

function formatDate(value: unknown) {
  const date = parseLocalDate(value);

  return date === null ? "" : dateFormatter.toDate(date);
}

function formatCategory(value: string) {
  const category = value.split("-")[0] ?? value;

  return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
}

function getUploadDate(upload: CaregiverDocumentUpload) {
  return parseLocalDate(upload.createdAt);
}

function isDateInRange(
  date: LocalDate | null,
  startDate: LocalDate | null,
  endDate: LocalDate | null
) {
  if (date === null) {
    return startDate === null && endDate === null;
  }

  if (startDate !== null && date.isBefore(startDate)) {
    return false;
  }

  if (endDate !== null && date.isAfter(endDate)) {
    return false;
  }

  return true;
}

function matchesFlagFilter(value: boolean, filter: FlagFilter) {
  return filter === "all" || (filter === "yes" ? value : !value);
}

function BooleanMark(props: { value: boolean }) {
  return props.value ? (
    <CheckIcon color="#4c91f7" fontSize="12px" />
  ) : (
    <CloseIcon color="#f15b5b" fontSize="10px" />
  );
}

function UploadStatusBadge(props: { document: CaregiverDocument }) {
  if (props.document.uploads.length > 0) {
    return <Badge colorScheme="blue">{props.document.uploads.length}</Badge>;
  }

  return (
    <Badge bg="#f45b69" color="#fff" textTransform="none">
      {props.document.isMandatory ? "Mandatory Missing" : "Missing"}
    </Badge>
  );
}

function DocumentActionMenu(props: {
  caregiver: CaregiverProfileCaregiver;
  actions: CaregiverProfileBridgeActions;
  upload: CaregiverDocumentUpload;
  onDone: () => void;
}) {
  const actionParams = {
    caregiver: props.caregiver,
    data: props.upload,
    onDone: props.onDone,
  };

  return (
    <Menu placement="bottom-end">
      {/* Chakra owns the trigger element here, so the bootstrap button classes are
          applied directly instead of going through EntityModalButton. */}
      <MenuButton
        aria-label="Document upload actions"
        className="btn btn-info"
        style={{
          alignItems: "center",
          borderRadius: "50%",
          display: "inline-flex",
          height: 32,
          justifyContent: "center",
          minWidth: 32,
          padding: 0,
          width: 32,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <i className="fa fa-ellipsis-v" />
      </MenuButton>
      <MenuList fontSize="14px" minW="170px" py={0} onClick={(event) => event.stopPropagation()}>
        <MenuItem onClick={() => props.actions.viewDocumentUpload(actionParams)}>
          View file
        </MenuItem>
        <MenuItem onClick={() => props.actions.downloadDocumentUpload(actionParams)}>
          Download file
        </MenuItem>
        <MenuItem onClick={() => props.actions.sendDocumentUploadToEmail(actionParams)}>
          Send PDF to e-mail
        </MenuItem>
        <MenuItem onClick={() => props.actions.sendDocumentUploadToFax(actionParams)}>
          Send PDF to fax
        </MenuItem>
        <MenuItem color="red.500" onClick={() => props.actions.removeDocumentUpload(actionParams)}>
          Remove file
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

function UploadsExpandedRow(props: {
  actions: CaregiverProfileBridgeActions;
  caregiver: CaregiverProfileCaregiver;
  colSpan: number;
  document: CaregiverDocument;
  onDone: () => void;
}) {
  return (
    <Tr>
      <Td bg="#fff" borderColor="#ddd" colSpan={props.colSpan} px={5} py={4}>
        <VStack alignItems="stretch" spacing={3}>
          <Text fontSize="18px" fontWeight={400}>
            Uploads
          </Text>
          {props.document.uploads.length === 0 ? (
            <Text color={entityProfileModalColors.muted} fontWeight={600}>
              No uploads found for {props.document.documentTypeName}
            </Text>
          ) : (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th textTransform="none">Upload Date</Th>
                  {props.document.isExpires ? <Th textTransform="none">Expiry Date</Th> : null}
                  <Th textTransform="none">Uploaded By</Th>
                  <Th textAlign="center" textTransform="none">
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {props.document.uploads.map((upload) => (
                  <Tr key={upload.uploadId}>
                    <Td>{formatDate(upload.createdAt)}</Td>
                    {props.document.isExpires ? <Td>{formatDate(upload.expiryDate)}</Td> : null}
                    <Td>{upload.uploadedBy.fullName}</Td>
                    <Td textAlign="center">
                      <DocumentActionMenu
                        actions={props.actions}
                        caregiver={props.caregiver}
                        upload={upload}
                        onDone={props.onDone}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </VStack>
      </Td>
    </Tr>
  );
}

export default function CaregiverProfileDocumentsSection(props: Props) {
  const documentsQuery = useCaregiverUploadedDocuments(props.caregiverId);
  const caregiver = props.caregiver;
  const documents = React.useMemo(
    () => documentsQuery.data?.documents ?? [],
    [documentsQuery.data?.documents]
  );
  const [searchTerm, setSearchTerm] = React.useState("");
  const [hrFilter, setHrFilter] = React.useState<FlagFilter>("all");
  const [complianceFilter, setComplianceFilter] = React.useState<FlagFilter>("all");
  const [passportFilter, setPassportFilter] = React.useState<FlagFilter>("all");
  const [uploadedAtRange, setUploadedAtRange] = React.useState<
    [LocalDate | null, LocalDate | null]
  >([null, null]);
  const [expandedDocumentTypeIds, setExpandedDocumentTypeIds] = React.useState<Set<number>>(
    () => new Set()
  );
  const hasInitializedDateRange = React.useRef(false);

  const uploadDates = React.useMemo(
    () =>
      documents
        .flatMap((document) => document.uploads.map(getUploadDate))
        .filter((date) => date !== null),
    [documents]
  );

  React.useEffect(() => {
    if (hasInitializedDateRange.current || uploadDates.length === 0) {
      return;
    }

    hasInitializedDateRange.current = true;
    setUploadedAtRange([
      uploadDates.reduce((earliest, date) => (date.isBefore(earliest) ? date : earliest)),
      uploadDates.reduce((latest, date) => (date.isAfter(latest) ? date : latest)),
    ]);
  }, [uploadDates]);

  const filteredDocuments = React.useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const [startDate, endDate] = uploadedAtRange;

    return documents.filter((document) => {
      const matchesSearch =
        normalizedSearchTerm === "" ||
        [document.category, document.documentTypeName]
          .map((value) => (value === document.category ? formatCategory(value) : value))
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearchTerm);
      const matchesDate =
        startDate === null && endDate === null
          ? true
          : document.uploads.some((upload) =>
              isDateInRange(getUploadDate(upload), startDate, endDate)
            );

      return (
        matchesSearch &&
        matchesDate &&
        matchesFlagFilter(document.isHr, hrFilter) &&
        matchesFlagFilter(document.isCompliance, complianceFilter) &&
        matchesFlagFilter(document.isPassport, passportFilter)
      );
    });
  }, [complianceFilter, documents, hrFilter, passportFilter, searchTerm, uploadedAtRange]);

  const columns = React.useMemo<
    Array<EntityProfileDataTableColumn<CaregiverDocument, DocumentSortKey>>
  >(
    () => [
      {
        key: "category",
        header: "Category",
        render: (document) => formatCategory(document.category),
        sortKey: "category",
        sortValue: (document) => formatCategory(document.category),
      },
      {
        key: "documentType",
        header: "Document Type",
        render: (document) => document.documentTypeName,
        sortKey: "documentType",
        sortValue: (document) => document.documentTypeName,
      },
      {
        key: "uploads",
        header: "Uploads",
        render: (document) => <UploadStatusBadge document={document} />,
        sortKey: "uploads",
        sortValue: (document) => document.uploads.length,
      },
      {
        key: "isHr",
        cellProps: { textAlign: "center" },
        header: "Is For HR",
        render: (document) => <BooleanMark value={document.isHr} />,
        sortKey: "isHr",
        sortValue: (document) => Number(document.isHr),
      },
      {
        key: "isCompliance",
        cellProps: { textAlign: "center" },
        header: "Is For Compliance",
        render: (document) => <BooleanMark value={document.isCompliance} />,
        sortKey: "isCompliance",
        sortValue: (document) => Number(document.isCompliance),
      },
      {
        key: "isPassport",
        cellProps: { textAlign: "center" },
        header: "Is For Passport",
        render: (document) => <BooleanMark value={document.isPassport} />,
        sortKey: "isPassport",
        sortValue: (document) => Number(document.isPassport),
      },
      {
        key: "addUpload",
        cellProps: { textAlign: "left", whiteSpace: "nowrap" },
        header: "Add Upload",
        render: (document) =>
          caregiver === undefined ? null : (
            <EntityModalButton
              kind="blue"
              size="xs"
              onClick={(event) => {
                event.stopPropagation();
                props.actions.addDocumentUpload({
                  caregiver,
                  data: document,
                  onDone: () => void documentsQuery.refetch(),
                });
              }}
            >
              Add Upload
            </EntityModalButton>
          ),
      },
    ],
    [caregiver, documentsQuery, props.actions]
  );

  const toggleExpanded = React.useCallback((documentTypeId: number) => {
    setExpandedDocumentTypeIds((current) => {
      const next = new Set(current);

      if (next.has(documentTypeId)) {
        next.delete(documentTypeId);
      } else {
        next.add(documentTypeId);
      }

      return next;
    });
  }, []);

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load caregiver documents."
      isError={documentsQuery.isError}
      isLoading={documentsQuery.isLoading}
      loadingLabel="Loading caregiver documents"
      onRetry={() => void documentsQuery.refetch()}
    >
      {documents.length === 0 ? (
        <EntityProfileEmptyState>No caregiver documents.</EntityProfileEmptyState>
      ) : (
        <VStack alignItems="stretch" spacing={4}>
          <EntityProfileFilterBar>
            <EntityProfileFilterBarItem flex="0 0 340px" label="Search" maxW="340px">
              <InputGroup size="sm">
                <Input
                  borderColor="#cfcfcf"
                  borderRadius="3px"
                  h="34px"
                  placeholder="Search document"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="#111827" />
                </InputLeftElement>
              </InputGroup>
            </EntityProfileFilterBarItem>
            <EntityProfileFilterBarItem flex="0 0 92px" label="Is For HR" maxW="92px">
              <Select
                buttonProps={filterSelectButtonProps}
                label="Select"
                multiple={false}
                options={flagFilterOptions}
                size="sm"
                value={hrFilter}
                width="100%"
                onChange={(value) => setHrFilter(value ?? "all")}
              />
            </EntityProfileFilterBarItem>
            <EntityProfileFilterBarItem flex="0 0 132px" label="Is For Compliance" maxW="132px">
              <Select
                buttonProps={filterSelectButtonProps}
                label="Select"
                multiple={false}
                options={flagFilterOptions}
                size="sm"
                value={complianceFilter}
                width="100%"
                onChange={(value) => setComplianceFilter(value ?? "all")}
              />
            </EntityProfileFilterBarItem>
            <EntityProfileFilterBarItem flex="0 0 120px" label="Is For Passport" maxW="120px">
              <Select
                buttonProps={filterSelectButtonProps}
                label="Select"
                multiple={false}
                options={flagFilterOptions}
                size="sm"
                value={passportFilter}
                width="100%"
                onChange={(value) => setPassportFilter(value ?? "all")}
              />
            </EntityProfileFilterBarItem>
            <EntityProfileFilterBarItem flex="0 0 220px" label="Uploaded at" maxW="220px">
              <RangeDatePicker
                endDate={uploadedAtRange[1]}
                inputProps={{
                  h: "34px",
                  borderRadius: "3px",
                  borderColor: "#cfcfcf",
                  fontSize: "14px",
                }}
                startDate={uploadedAtRange[0]}
                onChange={(date) => {
                  if (date === null) {
                    setUploadedAtRange([null, null]);
                    return;
                  }

                  setUploadedAtRange(date);
                }}
              />
            </EntityProfileFilterBarItem>
          </EntityProfileFilterBar>
          <EntityProfileDataTable
            columns={columns}
            defaultPageSize={10}
            defaultSort={{ key: "category", direction: "asc" }}
            getRowKey={(document) => document.documentTypeId}
            pageSizeOptions={[10, 25, 50, 100]}
            renderExpandedRow={(document) =>
              expandedDocumentTypeIds.has(document.documentTypeId) ? (
                caregiver === undefined ? null : (
                  <UploadsExpandedRow
                    actions={props.actions}
                    caregiver={caregiver}
                    colSpan={columns.length}
                    document={document}
                    onDone={() => void documentsQuery.refetch()}
                  />
                )
              ) : null
            }
            rowProps={(document) => ({
              cursor: "pointer",
              onClick: () => toggleExpanded(document.documentTypeId),
            })}
            rows={filteredDocuments}
            showEmptyState={false}
            tableSx={{
              "& th": { textAlign: "left" },
              "& td": { fontSize: "14px" },
            }}
          />
        </VStack>
      )}
    </EntityProfileQuerySection>
  );
}
